import React from 'react';
import { useParams } from 'react-router-dom';
import bearing from '@turf/bearing';

import { useDebriefDispatch, useDebriefSelector } from 'ff/debrief/hooks';
import {
    extractTrimmedValues,
    findNearestPoint,
    flattenDebriefSegments,
    getSegmentId,
    isCurrentSegment,
    trackPointToGeojsonPoint,
} from 'ff/debrief/utils';
import * as store from 'ff/debrief/features/ChronoSlice';
import * as api from 'ff/debrief/features/TracklogApi';

export function useChrono() {
    const state = useDebriefSelector(s => s.chronoSlice);
    const dispatch = useDebriefDispatch();
    const { actions } = store;

    const params = useParams<{ tracklogId: string }>();
    const tracklogId = params.tracklogId;
    const { data: tl } = api.useGetTracklogByIdQuery(tracklogId, {
        skip: !tracklogId,
    });
    const { data: debrief } = api.useGetDebriefByTrackUUIDQuery(tracklogId, {
        skip: !tracklogId,
    });

    const trimmedValues = extractTrimmedValues(tl);
    const currentTime = trimmedValues?.timestampStart + state.currentOffset;
    const points = tl?.points?.data;

    const currentPoint = React.useMemo(() => {
        if (!points) return null;
        const idx = findNearestPoint(points, currentTime);
        const nearestTrackPoint = points[idx];
        const nextTrackPoint = points[idx + 1];
        const prevTrackPoint = points[idx - 1];
        const nearestPoint = trackPointToGeojsonPoint(nearestTrackPoint);
        const nextPoint = trackPointToGeojsonPoint(nextTrackPoint);
        const prevPoint = trackPointToGeojsonPoint(prevTrackPoint);
        const currentBearing = nextTrackPoint
            ? bearing(nearestPoint, nextPoint)
            : bearing(prevPoint, nearestPoint);
        return {
            timestamp: nearestTrackPoint[0],
            latitude: nearestTrackPoint[1],
            longitude: nearestTrackPoint[2],
            groundSpeed: nearestTrackPoint[3],
            altitudeFeet: nearestTrackPoint[4],
            cabinAltitudeFeet: nearestTrackPoint[5],
            bankDeg: nearestTrackPoint[6],
            pitchDeg: nearestTrackPoint[7],
            gLoad: nearestTrackPoint[8],
            bearing: currentBearing,
        };
    }, [currentTime, points]);

    const segments = debrief?.debriefSegments;
    const currentSegmentId = React.useMemo(() => {
        // @ts-ignore
        const lastSegment = flattenDebriefSegments(segments).findLast(seg =>
            isCurrentSegment(state.currentOffset, seg)
        );
        return lastSegment && getSegmentId(lastSegment);
    }, [segments, state.currentOffset]);

    const play = () => {
        dispatch(store.playTicker());
    };

    const pause = () => {
        dispatch(actions.pauseTicker());
    };

    const setOffset = (offset: number) => {
        dispatch(actions.setOffset(offset));
    };

    const setSpeed = (speed: number) => {
        dispatch(store.setSpeed(speed));
    };

    const resetChrono = () => {
        dispatch(actions.reset());
    };

    return {
        offset: state.currentOffset,
        speed: state.tickerSpeed,
        currentTime: currentTime,
        currentPoint: currentPoint,
        currentSegmentId: currentSegmentId,
        isTicking: Boolean(state.ticker),
        play: play,
        pause: pause,
        setOffset: setOffset,
        setSpeed: setSpeed,
        resetChrono: resetChrono,
    };
}
